/**
 * Pure conversion between the backend graph doc shape and React Flow's node/edge shape.
 * Same conversion path for Load, Draft-accept, and a running test's live state —
 * there is no separate "preview" graph state (ref: spec §7).
 */
import type { Node, Edge } from 'reactflow';
import type { GraphNode, GraphEdge, BranchCondition, WorkflowConfigDocument } from './workflowGraphTypes';
import { formatEdgeConditionLabel } from './edgeConditionUtils';

export const PRIMITIVE_NODE_RF_TYPE = 'primitiveNode';

export function docToRfNodes(doc: Pick<WorkflowConfigDocument, 'nodes'>): Node[] {
  return doc.nodes.map((node, index) => ({
    id: node.id,
    type: PRIMITIVE_NODE_RF_TYPE,
    position: node.position ?? { x: 80 + (index % 4) * 220, y: 80 + Math.floor(index / 4) * 160 },
    data: {
      // node.type is absent only for an unresolved tier-3 action reference from a draft
      // (ref: 06_...md §5) — every saved/compiled graph always has it.
      primType: node.type,
      name: node.name,
      // NEW — server-computed, always a good human-readable header; read-only (rfToDoc never
      // sends it back — see below).
      displayLabel: node.display_label,
      config: node.config ?? {},
      // NEW — action-authored fields. As of the 2026-07-24 API update, the backend returns
      // action_id/inputs on every GET /{name} response too (not just in-session/draft nodes),
      // so the friendly catalog-authored form now survives a reload — no client-side tracking
      // of "which action authored this node" is needed (ref: Frontend action items 2026-07-24 §2).
      actionId: node.action_id,
      inputs: node.inputs,
      assumed: node.assumed ?? false,
    },
  }));
}

export function docToRfEdges(doc: Pick<WorkflowConfigDocument, 'edges'>): Edge[] {
  return doc.edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    // PR #669 review (C1) — condition_op now carried through on load too (previously dropped,
    // silently stripping any structured comparison the backend returned, e.g. from an AI draft).
    label: formatEdgeConditionLabel(edge.condition, edge.condition_op),
    data: { condition: edge.condition, condition_op: edge.condition_op ?? null },
    // Background chip so a condition label ("approved", "timeout"...) stays legible even when
    // the edge crosses near/behind a node — cosmetic only, rfToDoc never reads these back out.
    labelStyle: { fontSize: 11, fontWeight: 700, fill: '#37474f' },
    labelBgStyle: { fill: '#ffffff', fillOpacity: 0.92 },
    labelBgPadding: [6, 3] as [number, number],
    labelBgBorderRadius: 4,
  }));
}

export function rfToDoc(nodes: Node[], edges: Edge[]): Pick<WorkflowConfigDocument, 'nodes' | 'edges'> {
  const graphNodes: GraphNode[] = nodes.map((node) => {
    // display_label is read-only/server-computed — intentionally not included here; the server
    // recomputes it fresh from name/type on every response, so it's never round-tripped.
    const base = {
      id: node.id,
      name: node.data?.name ?? node.id,
      assumed: Boolean(node.data?.assumed),
      position: node.position,
    };
    // Action-authored node: send action_id/inputs, omit type/config entirely — the server
    // fills those in on compile and silently overwrites anything sent here (ref: 06_...md §3).
    // action_id/inputs now also round-trip back on reload (ref: Frontend action items 2026-07-24 §2).
    if (node.data?.actionId) {
      return { ...base, action_id: node.data.actionId, inputs: node.data?.inputs ?? {} };
    }
    // Raw primitive node — unchanged from before the catalog existed.
    return { ...base, type: node.data?.primType, config: node.data?.config ?? {} };
  });

  const graphEdges: GraphEdge[] = edges.map((edge) => {
    const conditionOp = (edge.data?.condition_op as BranchCondition | null | undefined) ?? null;
    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      condition: (edge.data?.condition as string | null | undefined) ?? null,
      // PR #669 review (C1) — condition_op now carried through on save too (previously dropped
      // entirely; rfToDoc only ever wrote `condition`).
      //
      // PR #684 review (M1) — the orchestrator's validator "explicitly rejects any edge carrying
      // condition_op" today (edgeConditionUtils.ts), which reads as a key-presence check, not a
      // non-null-value check. Sending `condition_op: null` on every edge (the previous behavior)
      // risked breaking every save/validate/run-test. Omit the key entirely when there's no real
      // comparison, so only an edge that actually carries one sends it at all.
      ...(conditionOp !== null && { condition_op: conditionOp }),
    };
  });

  return { nodes: graphNodes, edges: graphEdges };
}
